import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  LIBELLES_ORIGINE_EXTRAIT,
  RECHERCHE_MIN,
  RESULTATS_MAX,
  motsClesDe,
  plierTexte,
  type AnnonceResume,
  type ReponseRecherche,
} from '@rh/shared';
import { appel } from '../api';
import { BadgeAdequation } from '../composants/Adequation';
import { Chargement, Erreur, Vide } from '../composants/Etat';
import { Etoile } from '../composants/Etoile';
import { Note, Pastille } from '../composants/Pastille';
import { Statut } from '../composants/Statut';
import { TriListe } from '../composants/TriListe';
import { ecrireParcours } from '../parcours';
import { trier, type Tri } from '../tri-liste';
import { classeNom, libelleSource, nomComplet } from '../format';

/** Mots trouvés mis en évidence dans l'extrait, accents et casse ignorés. */
function surligner(texte: string, motsCles: readonly string[]): ReactNode {
  const plie = plierTexte(texte);
  const marques: Array<[number, number]> = [];
  for (const mot of motsCles) {
    for (let debut = plie.indexOf(mot); debut >= 0; debut = plie.indexOf(mot, debut + mot.length)) {
      marques.push([debut, debut + mot.length]);
    }
  }
  if (marques.length === 0) return texte;
  marques.sort((a, b) => a[0] - b[0]);

  const morceaux: ReactNode[] = [];
  let curseur = 0;
  for (const [debut, fin] of marques) {
    if (debut < curseur) continue; // chevauchement : la première marque gagne
    if (debut > curseur) morceaux.push(texte.slice(curseur, debut));
    morceaux.push(<mark key={debut}>{texte.slice(debut, fin)}</mark>);
    curseur = fin;
  }
  if (curseur < texte.length) morceaux.push(texte.slice(curseur));
  return morceaux.map((morceau, i) => <Fragment key={i}>{morceau}</Fragment>);
}

/**
 * Écran de recherche par mot-clé (demande du 2026-09-19, docs/UI.md § Écran 7).
 * Le mot est cherché dans les postulations — coordonnées, synthèse, annonce —
 * et dans le texte des CV et lettres déjà extrait à l'ingestion. Plusieurs
 * mots se cumulent : un dossier n'est rendu que s'il les porte tous.
 */
export function Recherche() {
  const [recherche, setRecherche] = useSearchParams();
  const q = recherche.get('q') ?? '';
  const annonceId = recherche.get('annonce') ?? '';
  const [saisie, setSaisie] = useState(q);
  const [reponse, setReponse] = useState<ReponseRecherche | null>(null);
  const [annonce, setAnnonce] = useState<AnnonceResume | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);
  const [tri, setTri] = useState<Tri>('defaut');

  useEffect(() => setSaisie(q), [q]);

  useEffect(() => {
    if (!annonceId) return setAnnonce(null);
    appel<AnnonceResume[]>('/annonces')
      .then((liste) => setAnnonce(liste.find((a) => a.id === annonceId) ?? null))
      .catch(() => setAnnonce(null));
  }, [annonceId]);

  useEffect(() => {
    if (motsClesDe(q).length === 0) {
      setReponse(null);
      setErreur(null);
      return;
    }
    let vivant = true;
    setChargement(true);
    setErreur(null);
    const chemin = `/recherche?${new URLSearchParams(annonceId ? { q, annonce: annonceId } : { q }).toString()}`;
    appel<ReponseRecherche>(chemin)
      .then((lue) => vivant && setReponse(lue))
      .catch((e: Error) => vivant && setErreur(e.message))
      .finally(() => vivant && setChargement(false));
    return () => {
      vivant = false;
    };
  }, [q, annonceId]);

  const resultats = useMemo(() => {
    if (!reponse) return [];
    const ordonnees = trier(
      reponse.resultats.map((r) => r.candidature),
      tri,
    );
    const parId = new Map(reponse.resultats.map((r) => [r.candidature.id, r]));
    return ordonnees.map((c) => parId.get(c.id)!);
  }, [reponse, tri]);

  // Les flèches de la fiche suivent l'ordre des résultats (2026-09-19).
  useEffect(() => {
    ecrireParcours({
      retour: `/recherche${recherche.toString() ? `?${recherche}` : ''}`,
      libelle: q ? `Recherche « ${q} »` : 'Recherche',
      ids: resultats.map(({ candidature }) => candidature.id),
    });
  }, [resultats, recherche, q]);

  function lancer(motsCles: string): void {
    const parametres: Record<string, string> = {};
    if (motsCles.trim()) parametres.q = motsCles.trim();
    if (annonceId) parametres.annonce = annonceId;
    setRecherche(new URLSearchParams(parametres), { replace: true });
  }

  function changerPerimetre(surCetteAnnonce: boolean): void {
    const parametres: Record<string, string> = {};
    if (q) parametres.q = q;
    if (surCetteAnnonce && annonce) parametres.annonce = annonce.id;
    setRecherche(new URLSearchParams(parametres), { replace: true });
  }

  const motsCles = reponse?.motsCles ?? [];
  const trop = saisie.trim().length > 0 && motsClesDe(saisie).length === 0;

  return (
    <section>
      <h2>Recherche par mot-clé</h2>
      <form
        className="barre-recherche"
        role="search"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          lancer(saisie);
        }}
      >
        <label className="champ">
          <span>Mot-clé</span>
          <input
            type="search"
            value={saisie}
            autoFocus
            maxLength={200}
            placeholder="caissier, permis B, « service client »…"
            onChange={(evenement) => setSaisie(evenement.target.value)}
          />
        </label>
        <button type="submit" className="bouton" disabled={trop}>
          Rechercher
        </button>
      </form>
      <p className="mention">
        Cherché dans les candidatures et dans le texte des CV et lettres. Plusieurs mots :
        tous doivent s’y trouver. Une expression entre guillemets reste d’un bloc.
      </p>
      {trop && <p className="signal">Un mot-clé fait au moins {RECHERCHE_MIN} caractères.</p>}
      {annonce && (
        <label className="case interrupteur">
          <input
            type="checkbox"
            role="switch"
            checked={Boolean(annonceId)}
            onChange={(evenement) => changerPerimetre(evenement.target.checked)}
          />
          <span>Seulement l’annonce « {annonce.intitule} »</span>
        </label>
      )}

      {erreur && <Erreur message={erreur} />}
      {chargement && <Chargement quoi="des résultats" />}
      {reponse && !chargement && (
        <>
          <div className="entete-annonce">
            <p className="resume">
              {reponse.resultats.length === 0
                ? 'Aucun dossier'
                : `${reponse.resultats.length}${reponse.tronquee ? ' premiers' : ''} dossier${
                    reponse.resultats.length > 1 ? 's' : ''
                  }`}{' '}
              pour {motsCles.map((mot) => `« ${mot} »`).join(' et ')}
            </p>
            {reponse.resultats.length > 0 && (
              <TriListe tri={tri} onChange={setTri} libelleDefaut="Réception, plus récente d’abord" />
            )}
          </div>
          {reponse.tronquee && (
            <p className="signal">
              Liste arrêtée à {RESULTATS_MAX} dossiers : ajoutez un mot-clé pour l’affiner.
            </p>
          )}
          {reponse.resultats.length === 0 && (
            <Vide enfants="Aucune candidature ne porte ces mots. Un CV dont le texte n’a pas pu être extrait ne se trouve pas par son contenu." />
          )}
        </>
      )}

      <ul className="candidats resultats-recherche">
        {resultats.map(({ candidature, extraits }) => (
          <li key={candidature.id} className="carte-candidat">
            <Link to={`/candidatures/${candidature.id}`} className="carte-lien">
              <Pastille valeur={candidature.potentiel} libelle etiquette={candidature.etiquette} />
              <div className="corps">
                <div className="ligne1">
                  <Etoile potentiel={candidature.potentiel} />
                  <strong className={classeNom(candidature.statut)}>{nomComplet(candidature)}</strong>
                  <BadgeAdequation valeur={candidature.adequation} />
                </div>
                <div className="meta">
                  {candidature.statut !== 'ecarte' && <Statut valeur={candidature.statut} />}
                  {candidature.ville && <span>{candidature.ville}</span>}
                  <span>{libelleSource(candidature.source)}</span>
                  <span>{new Date(candidature.recueLe).toLocaleDateString('fr-FR')}</span>
                  {candidature.annonceReference && <span className="origine">{candidature.annonceReference}</span>}
                </div>
                {candidature.annonceIntitule && <p className="synthese">{candidature.annonceIntitule}</p>}
                <ul className="extraits">
                  {extraits.map((extrait, rang) => (
                    <li key={rang}>
                      <span className="origine-extrait">
                        {extrait.origine === 'cv' || extrait.origine === 'lettre'
                          ? `${LIBELLES_ORIGINE_EXTRAIT[extrait.origine]} — ${extrait.libelle}`
                          : LIBELLES_ORIGINE_EXTRAIT[extrait.origine]}
                      </span>
                      <q>{surligner(extrait.texte, motsCles)}</q>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="coin-droit">
                {candidature.statut === 'ecarte' && <Statut valeur={candidature.statut} />}
                <Note valeur={candidature.noteEntretien} date={candidature.noteLe} />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
